import {useMemo} from 'react';
import {assert} from 'assert-ts';
import {useAppSelector} from 'store/hooks';
import type {CodeList, CodeListId, CodeListsState} from '../types';
import {selectCodelists} from '../codelistsSlice';
import {useRestrictedCodelistsContext} from '../contexts';
import {getDummyCodeList} from './getDummyCodeList';

export const useCodelist = (
  id: CodeListId,
  ignoreRestrictions?: boolean,
): CodeList => {
  const {getRestrictedCodelist, getLocalCodelist} =
    useRestrictedCodelistsContext();
  const codeListState: CodeListsState = useAppSelector(selectCodelists);
  return useMemo(() => {
    const codeList = codeListState[id]?.data ?? getLocalCodelist(id);
    if (assert.soft(codeList, 'useCodeList: Code list not loaded', {id})) {
      // Advanced search allows all values.
      if (ignoreRestrictions) {
        return {
          ...codeList,
          codes: codeList.codes.map(code => ({
            ...code,
            deactivated: false,
            disabled: false,
          })),
        };
      }

      return getRestrictedCodelist(codeList);
    }
    return getDummyCodeList(id);
  }, [
    codeListState,
    getLocalCodelist,
    getRestrictedCodelist,
    id,
    ignoreRestrictions,
  ]);
};
